import { useMemo } from 'react'
import { format } from 'date-fns'
import { Cell, Column, Row, TableInstance, useTable } from 'react-table'
import {
  Badge,
  Button,
  HStack,
  IconButton,
  Td,
  Tr,
  useClipboard,
} from '@ritualco/jutsu'
import { ReactComponent as DeleteIcon } from 'assets/Icons/Trash-Can.svg'
import { QuestionTooltip } from 'components/QuestionTooltip'
import { useAnalytics } from 'lib/simple-analytics'
import { useContent } from 'lib/content/react'
import { useDeleteMerchantList } from 'api/endpoints/merchantLists'
import { useStore } from 'state/store'

import { RestaurantList, RestaurantType } from 'types/entities'
import { AnalyticEvents } from './types'
import { Link } from 'lib/router'

type UseMyListsTableDeps = {
  restaurantLists: RestaurantList[]
}
type FnUseMyListsTable = (
  params: UseMyListsTableDeps
) => TableInstance<RestaurantList>

const CopyLink = (props: { value: string }) => {
  const { hasCopied, onCopy } = useClipboard(props.value)
  const { track } = useAnalytics()
  const { formatMessage } = useContent()

  return (
    <Button
      variant="link"
      onClick={() => {
        track(AnalyticEvents.rowActionCopyLinkClicked, { value: props.value })
        onCopy()
      }}
    >
      {hasCopied
        ? formatMessage({ id: 'pmportal.restaurantLists.copied' })
        : formatMessage({ id: 'pmportal.restaurantLists.copyURL' })}
    </Button>
  )
}

const Actions = ({
  listId,
  listType,
}: {
  listId: string
  listType: RestaurantType
}) => {
  const deleteList = useDeleteMerchantList()
  const workspaceId = useStore(state => state.workspace.id) as string
  const organizationId = useStore(state => state.organization.id) as string
  const { formatMessage } = useContent()
  return (
    <HStack spacing={2} justifyContent="flex-end">
      <Link to={`/workspaces/${workspaceId}/restaurants/lists/${listId}`}>
        <Button size="sm" colorScheme="transparent" variant="outline">
          {formatMessage({ id: 'pmportal.global.actionView' })}
        </Button>
      </Link>
      {listType !== 'allRestaurants' && (
        <IconButton
          aria-label={formatMessage({ id: 'pmportal.global.actionRemove' })}
          size="sm"
          icon={<DeleteIcon style={{ height: 18, width: 18 }} />}
          color="grayscale.secondary"
          colorScheme="transparent"
          variant="ghost"
          onClick={() => {
            deleteList.mutate({
              organizationId,
              workspaceId,
              merchantListId: listId,
            })
          }}
        />
      )}
    </HStack>
  )
}

export const useMyListsTable: FnUseMyListsTable = ({ restaurantLists }) => {
  const { formatMessage } = useContent()
  // Map restaurant type to content
  const typeContentMap = useMemo<Record<RestaurantType, string>>(
    () => ({
      allRestaurants: formatMessage({
        id: 'pmportal.restaurantLists.allRestaurants',
      }),
      generic: formatMessage({ id: 'pmportal.restaurantLists.generic' }),
    }),
    [formatMessage]
  )

  const columns = useMemo<Array<Column<RestaurantList>>>(
    () => [
      {
        accessor: 'listName',
        Header: formatMessage({ id: 'pmportal.restaurantLists.listName' }),
      },
      {
        accessor: 'type',
        Header: (
          <QuestionTooltip
            label={formatMessage({
              id: 'pmportal.restaurantLists.typeDetails',
            })}
            aria-label={formatMessage({ id: 'pmportal.restaurantLists.type' })}
          >
            {formatMessage({ id: 'pmportal.restaurantLists.type' })}
          </QuestionTooltip>
        ),
        Cell: ({ value }) => <Badge>{typeContentMap[value]}</Badge>,
      },
      {
        accessor: 'referencedRestaurantsLen',
        Header: formatMessage({
          id: 'pmportal.restaurantLists.numOfRestaurants',
        }),
      },
      {
        id: 'lastModified',
        Header: formatMessage({ id: 'pmportal.restaurantLists.lastModified' }),
        accessor: ({ lastModified }) =>
          !lastModified ? '-' : format(lastModified, 'dd/LL/yy'),
      },
      {
        accessor: 'listUrl',
        Header: (
          <QuestionTooltip
            label={formatMessage({
              id: 'pmportal.restaurantLists.listUrlDetails',
            })}
            aria-label={formatMessage({
              id: 'pmportal.restaurantLists.listUrl',
            })}
          >
            {formatMessage({ id: 'pmportal.restaurantLists.listUrl' })}
          </QuestionTooltip>
        ),
        Cell: ({ value }) => (!value ? '-' : <CopyLink value={value} />),
      },
      {
        id: 'actions',
        Cell: ({ row }: Cell<RestaurantList>) => (
          <Actions listId={row.original.id} listType={row.original.type} />
        ),
      },
    ],
    [typeContentMap, formatMessage]
  )

  const table = useTable({ columns, data: restaurantLists })

  return table
}

type FnRenderTbody = (
  prepareRow: (row: Row<RestaurantList>) => void
) => (row: Row<RestaurantList>) => JSX.Element
export const renderTbody: FnRenderTbody = prepare => row => {
  prepare(row)
  return (
    <Tr {...row.getRowProps()}>
      {row.cells.map(cell => (
        <Td {...cell.getCellProps()} lineHeight="inherit">
          {cell.render('Cell')}
        </Td>
      ))}
    </Tr>
  )
}
